import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel, SectionHead, SectionShell } from "./_shared";
import { Info, ArrowRight } from "lucide-react";

const PALETTE = ["#7C5CFC", "#8FA9E6", "#E9B872", "#F0B79E", "#4B9F55", "#B8A5FD"];

export function MoneyMap() {
  const navigate = useNavigate();
  const [sel, setSel] = useState(null);
  const q = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  return (
    <Panel className="p-5 sm:p-6" data-testid="money-map">
      <SectionHead eyebrow="Money Map"
        title={<span className="inline-flex items-center gap-1.5">Service revenue
          <span className="group relative"><Info className="h-3.5 w-3.5 text-pawkt-text-disabled" />
            <span className="pointer-events-none absolute left-1/2 top-5 z-10 w-56 -translate-x-1/2 rounded-xl bg-pawkt-text px-3 py-2 text-[11px] font-medium text-white opacity-0 shadow-floating transition-opacity group-hover:opacity-100">
              Service revenue = value of completed services. This can differ from “Collected” (payments received) in the hero.
            </span>
          </span>
        </span>}
        hint="Value of completed services, by category." />
      <SectionShell query={q}>
        {(d) => {
          const rows = (d.by_service || []).slice(0, 6).map((r, i) => ({ ...r, color: PALETTE[i % PALETTE.length] }));
          const total = rows.reduce((s, r) => s + (r.revenue || 0), 0);
          if (total <= 0) {
            return <p className="text-sm text-pawkt-text-secondary" data-testid="moneymap-empty">Your revenue breakdown will appear here once services are completed and billed.</p>;
          }
          const selected = rows.find((r) => r.name === sel);
          return (
            <div>
              <div className="font-display text-3xl font-extrabold text-pawkt-text tabular">{inr(total)}</div>
              <div className="text-xs text-pawkt-text-secondary mb-4">across {rows.length} service {rows.length === 1 ? "line" : "lines"}</div>
              <div className="flex h-3 w-full overflow-hidden rounded-full mb-4">
                {rows.map((r) => (
                  <div key={r.name} className="h-full transition-all first:rounded-l-full last:rounded-r-full"
                    style={{ width: `${(r.revenue / total) * 100}%`, background: r.color, opacity: sel && sel !== r.name ? 0.35 : 1 }} />
                ))}
              </div>
              <div className="space-y-1">
                {rows.map((r) => {
                  const pct = Math.round((r.revenue / total) * 100);
                  const isSel = r.name === sel;
                  return (
                    <button key={r.name} data-testid={`moneymap-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setSel(isSel ? null : r.name)} aria-pressed={isSel}
                      className={cn("group flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isSel ? "bg-pawkt-primary-soft" : "hover:bg-pawkt-soft")}>
                      <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: r.color }} />
                      <span className="flex-1 truncate text-sm font-semibold text-pawkt-text">{r.name}</span>
                      <span className="text-sm font-extrabold tabular text-pawkt-text">{inr(r.revenue)}</span>
                      <span className="w-9 text-right text-xs font-bold text-pawkt-text-secondary tabular">{pct}%</span>
                    </button>
                  );
                })}
              </div>
              {selected && (
                <div className="mt-3 rounded-2xl border border-pawkt-border-soft bg-pawkt-soft p-4 animate-fade-in" data-testid="moneymap-detail">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-base font-extrabold text-pawkt-text">{selected.name}</span>
                    <span className="font-display text-base font-extrabold tabular text-pawkt-primary-deep">{inr(selected.revenue)}</span>
                  </div>
                  <p className="mt-1 text-xs text-pawkt-text-secondary">{Math.round((selected.revenue / total) * 100)}% of completed-service value.</p>
                  <button onClick={() => navigate("/reports")} className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover">
                    View {selected.name} revenue <ArrowRight className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
